import React, { useEffect, useState } from 'react';
import { RatingBadge } from './RatingBadge';
import { ProsConsBox } from './ProsConsBox';
import { ExternalLink, Check, ChevronDown, ChevronUp, Quote } from 'lucide-react';
interface ProductCardProps {
  rank: number;
  brandName: string;
  tagline: string;
  rating: number;
  grade: string;
  summary: string;
  keyStrengths: string[];
  ratingsGrid: {
    effectiveness: number;
    formulaQuality: number;
    value: number;
    customerSatisfaction: number;
    returnPolicy: number;
  };
  pros: string[];
  cons: string[];
  testerQuote?: string;
  isTopPick?: boolean;
  imageUrl: string;
}
export function ProductCard({
  rank,
  brandName,
  tagline,
  rating,
  grade,
  summary,
  keyStrengths,
  ratingsGrid,
  pros,
  cons,
  testerQuote,
  isTopPick = false,
  imageUrl
}: ProductCardProps) {
  // Rank 1 is open by default, others collapsed to make the list scannable
  const [isExpanded, setIsExpanded] = useState(isTopPick);
  const [showAllStrengths, setShowAllStrengths] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // Auto-advance slideshow
  useEffect(() => {
    if (isPaused || !isExpanded) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % keyStrengths.length);
    }, 3000); // 3 seconds per slide
    return () => clearInterval(interval);
  }, [isPaused, isExpanded, keyStrengths.length]);
  return <div id={`product-rank-${rank}`} className={`group relative bg-white rounded-xl transition-all duration-300 overflow-hidden ${isTopPick ? 'ring-1 ring-[#C9A961] shadow-xl mb-12' // Extra spacing for top pick
  : 'border border-[#E8E4DD] shadow-sm hover:shadow-md mb-6'}`} data-id="element-108">
      {/* Editorial Top Border */}
      <div className={`h-1.5 w-full ${isTopPick ? 'bg-[#C9A961]' : 'bg-[#E8E4DD] group-hover:bg-[#C9A961]/50'}`} data-id="element-109" />

      {/* --- Main Scannable Section (Always Visible) --- */}
      <div className="p-5 md:p-8" data-id="element-110">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8" data-id="element-111">
          {/* Left Column: Image & Rank Badge */}
          <div className="flex-shrink-0 flex flex-row md:flex-col items-center md:items-start gap-4 md:w-48" data-id="element-112">
            <div className="relative" data-id="element-113">
              <div className={`w-24 h-24 md:w-48 md:h-48 rounded-lg bg-[#FAF8F5] flex items-center justify-center p-4 border border-[#E8E4DD]`} data-id="element-114">
                <img src={imageUrl} alt={brandName} className="max-w-full max-h-full object-contain mix-blend-multiply scale-200" data-id="element-115" />
              </div>
              {/* Rank Badge - Floating on Desktop, Inline on Mobile */}
              <div className="absolute -top-3 -left-3 md:-top-4 md:-left-4 shadow-lg" data-id="element-116">
                <div className={`flex flex-col items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full font-serif font-bold text-xl md:text-2xl border-4 border-white ${isTopPick ? 'bg-[#1A2332] text-[#C9A961]' : 'bg-[#E8E4DD] text-[#1A2332]'}`} data-id="element-117">
                  #{rank}
                </div>
              </div>
            </div>
            {/* Mobile only: Rating appears next to image */}
            <div className="md:hidden flex-1" data-id="element-118">
              <div className="flex items-center gap-2 mb-1" data-id="element-119">
                <span className="text-2xl font-bold text-[#1A2332]" data-id="element-120">
                  {rating}
                </span>
                <span className="text-xs text-[#1A2332]/60 uppercase tracking-wide" data-id="element-121">
                  /10 Rating
                </span>
              </div>
              <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${isTopPick ? 'bg-[#C9A961] text-white' : 'bg-[#E8E4DD] text-[#1A2332]/70'}`} data-id="element-122">
                Grade: {grade}
              </div>
            </div>
          </div>

          {/* Right Column: Content Header */}
          <div className="flex-1 min-w-0" data-id="element-123">
            {isTopPick && <span className="inline-block mb-3 px-3 py-1 bg-[#C9A961]/10 text-[#C9A961] text-[10px] md:text-xs font-bold tracking-widest uppercase rounded-sm" data-id="element-124">
                Editor's Choice - Best Overall
              </span>}

            {/* Title Block */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4" data-id="element-125">
              <div data-id="element-126">
                <h3 className="text-2xl md:text-3xl font-serif font-bold text-[#1A2332] leading-tight mb-2" data-id="element-127">
                  {brandName}
                </h3>
                <p className="text-sm md:text-base font-medium text-[#1A2332]/60 italic" data-id="element-128">
                  "{tagline}"
                </p>
              </div>
              {/* Desktop Rating Badge (Hidden on Mobile) */}
              <div className="hidden md:block pl-4 border-l border-[#E8E4DD]" data-id="element-129">
                <div className="flex flex-col items-end" data-id="element-130">
                  <span className="text-3xl font-bold text-[#1A2332] leading-none" data-id="element-131">
                    {rating}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-[#1A2332]/50 mb-2" data-id="element-132">
                    Score
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${isTopPick ? 'bg-[#C9A961] text-white' : 'bg-[#E8E4DD] text-[#1A2332]/70'}`} data-id="element-133">
                    Grade: {grade}
                  </span>
                </div>
              </div>
            </div>

            {/* The Verdict (Summary) */}
            <div className="mb-6" data-id="element-134">
              <p className="text-[#1A2332]/80 leading-relaxed text-sm md:text-base mb-4" data-id="element-135">
                <strong className="text-[#1A2332] font-bold block mb-1 uppercase text-xs tracking-wide opacity-70" data-id="element-136">
                  The Verdict
                </strong>
                {summary}
              </p>

              {/* CTA Button - Only for Faith & Fig, placed after summary */}
              {brandName.includes('Faith & Fig') && <a href="https://trysaffron.faithandfig.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#C9A961] hover:bg-[#B89851] text-white text-sm font-bold rounded-lg transition-all shadow-md hover:shadow-lg" data-id="element-137">
                  <span className="flex items-center gap-2" data-id="element-138">
                    Check Availability & Best Price
                    <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-139" />
                  </span>
                </a>}
            </div>

            {/* Key Strengths - Scannable Chips */}
            <div className="flex flex-wrap gap-2 mb-6" data-id="element-140">
              {(showAllStrengths ? keyStrengths : keyStrengths.slice(0, 3)).map((strength, idx) => <span key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#FAF8F5] border border-[#E8E4DD] text-[#1A2332]/70 text-xs font-medium" data-id="element-141">
                    <Check className="w-3 h-3 text-[#C9A961]" data-id="element-142" />
                    {strength}
                  </span>)}
              {keyStrengths.length > 3 && <button onClick={() => setShowAllStrengths(!showAllStrengths)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-[#C9A961] hover:text-[#B89851] hover:bg-[#C9A961]/5 rounded-md transition-colors" data-id="element-143">
                  {showAllStrengths ? <>
                      Show less
                      <ChevronUp className="w-3 h-3" data-id="element-144" />
                    </> : <>
                      +{keyStrengths.length - 3} more
                      <ChevronDown className="w-3 h-3" data-id="element-145" />
                    </>}
                </button>}
            </div>

            {/* Toggle Button for Details */}
            <button onClick={() => setIsExpanded(!isExpanded)} className="w-full md:w-auto flex items-center justify-center gap-2 text-sm font-bold text-[#C9A961] hover:text-[#B89851] transition-colors py-2 group" data-id="element-146">
              {isExpanded ? 'Hide Full Analysis' : 'Read Full Analysis & Specs'}
              {isExpanded ? <ChevronUp className="w-4 h-4 transition-transform group-hover:-translate-y-0.5" data-id="element-147" /> : <ChevronDown className="w-4 h-4 transition-transform group-hover:translate-y-0.5" data-id="element-148" />}
            </button>
          </div>
        </div>
      </div>

      {/* --- Expandable Details Section --- */}
      <div className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-149">
        <div className="border-t border-[#E8E4DD] bg-[#FAF8F5] p-5 md:p-8" data-id="element-150">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-151">
            {/* Left Column of Expanded: Ratings & Tester Notes */}
            <div className="lg:col-span-5 space-y-6" data-id="element-152">
              {/* Performance Grid */}
              <div className="bg-white p-5 rounded-lg border border-[#E8E4DD] shadow-sm" data-id="element-153">
                <h4 className="font-bold text-[#1A2332] text-xs uppercase tracking-widest mb-4" data-id="element-154">
                  Detailed Scoring
                </h4>
                <div className="space-y-3" data-id="element-155">
                  {Object.entries(ratingsGrid).map(([key, value]) => <div key={key} data-id="element-156">
                      <div className="flex justify-between text-xs mb-1" data-id="element-157">
                        <span className="capitalize text-[#1A2332]/70" data-id="element-158">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <span className="font-bold text-[#1A2332]" data-id="element-159">
                          {value}
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-[#E8E4DD] rounded-full overflow-hidden" data-id="element-160">
                        <div className="h-full bg-[#C9A961]" style={{
                      width: `${value * 10}%`
                    }} data-id="element-161"></div>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Tester Quote */}
              {testerQuote && <div className="relative p-6 bg-gradient-to-br from-[#FAF8F5] to-[#F5F1E8] rounded-lg border-2 border-[#E8E4DD] shadow-sm" data-id="element-162">
                  <Quote className="absolute top-3 left-3 w-8 h-8 text-[#C9A961] opacity-20" data-id="element-163" />
                  <p className="relative z-10 text-sm italic leading-relaxed text-[#1A2332]/80 pl-6 border-l-2 border-[#C9A961]" data-id="element-164">
                    "{testerQuote}"
                  </p>
                </div>}
            </div>

            {/* Right Column of Expanded: Pros/Cons & CTA */}
            <div className="lg:col-span-7 space-y-6" data-id="element-165">
              <ProsConsBox pros={pros} cons={cons} data-id="element-166" />

              {/* Minimalistic Feature Slideshow */}
              <div className="bg-white border border-[#E8E4DD] p-5 rounded-xl" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-167">
                <h5 className="text-xs font-bold text-[#1A2332] uppercase tracking-[0.16em] mb-3" data-id="element-168">
                  Complete Feature List
                </h5>

                {/* Slideshow Container */}
                <div className="mb-4" data-id="element-169">
                  {keyStrengths.map((strength, index) => <div key={index} className={`
          ${index === currentSlide ? 'block' : 'hidden'}
        `} data-id="element-170">
                      <div className="max-w-sm mx-auto bg-[#FAF8F5] border border-[#E8E4DD] rounded-lg px-3 py-2.5 flex items-start gap-2.5" data-id="element-171">
                        <Check className="w-4 h-4 mt-0.5 text-[#C9A961] flex-shrink-0" data-id="element-172" />
                        <span className="text-[13px] leading-relaxed text-[#1A2332]/85 text-left sm:text-center" data-id="element-173">
                          {strength}
                        </span>
                      </div>
                    </div>)}
                </div>

                {/* Navigation Dots */}
                <div className="flex items-center justify-center gap-1.5" data-id="element-174">
                  {keyStrengths.map((_, index) => <button key={index} onClick={() => setCurrentSlide(index)} className={`
          transition-all duration-300 rounded-full
          ${index === currentSlide ? 'w-6 h-1.5 bg-[#C9A961]' : 'w-1.5 h-1.5 bg-[#E8E4DD] hover:bg-[#C9A961]/50'}
        `} aria-label={`Go to feature ${index + 1}`} data-id="element-175" />)}
                </div>
              </div>

              {/* CTA - Only for Faith & Fig (Rank 1) */}
              {brandName.includes('Faith & Fig') && <a href="https://trysaffron.faithandfig.com" target="_blank" rel="noopener noreferrer" className="block w-full text-center px-6 py-4 bg-[#C9A961] hover:bg-[#B89851] text-white text-sm font-bold rounded-lg transition-all shadow-md hover:shadow-lg" data-id="element-176">
                  <span className="flex items-center justify-center gap-2" data-id="element-177">
                    Check Availability & Best Price
                    <ExternalLink className="w-4 h-4" data-id="element-178" />
                  </span>
                </a>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}